import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaCertificate, FaCheck, FaTimes, FaClock, FaUser, FaSearch, FaFilter } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function CertificateManagement({ token }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/certificate-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading certificate requests:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleApprove = async (requestId) => {
    if (!window.confirm('آیا از تایید این درخواست اطمینان دارید؟')) return;
    
    setProcessing(requestId);
    try {
      await axios.put(
        `${API_URL}/api/admin/certificate-requests/${requestId}/approve`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('درخواست گواهی تایید شد');
      loadRequests();
    } catch (error) {
      alert('خطا در تایید درخواست');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('آیا از رد این درخواست اطمینان دارید؟')) return;
    
    setProcessing(requestId);
    try {
      await axios.put(
        `${API_URL}/api/admin/certificate-requests/${requestId}/reject`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('درخواست گواهی رد شد');
      loadRequests();
    } catch (error) {
      alert('خطا در رد درخواست');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400', icon: <FaClock />, text: 'در انتظار' },
      approved: { color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400', icon: <FaCheck />, text: 'تایید شده' },
      rejected: { color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400', icon: <FaTimes />, text: 'رد شده' }
    };
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg font-bold text-sm ${badge.color}`}>
        {badge.icon}
        {badge.text}
      </span>
    );
  };

  // Filter and search logic
  const filteredRequests = requests.filter(req => {
    // Filter by status
    if (filterStatus !== 'all' && req.status !== filterStatus) {
      return false;
    }
    
    // Search by name, mobile, or idea title
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${req.registration_info?.first_name} ${req.registration_info?.last_name}`.toLowerCase();
      const mobile = req.user_mobile?.toLowerCase() || '';
      const ideaTitle = req.registration_info?.idea_title?.toLowerCase() || '';
      
      return fullName.includes(searchLower) || 
             mobile.includes(searchLower) || 
             ideaTitle.includes(searchLower);
    }
    
    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 rounded-xl p-4 border-2 border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
              <FaClock className="text-2xl text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{pendingCount}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">در انتظار بررسی</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border-2 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
              <FaCheck className="text-2xl text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{approvedCount}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">تایید شده</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-xl p-4 border-2 border-red-200 dark:border-red-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
              <FaTimes className="text-2xl text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{rejectedCount}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">رد شده</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search */}
      <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-lg">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Filter by Status */}
          <div className="flex-1">
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <FaFilter className="text-primary-500" />
              فیلتر بر اساس وضعیت
            </label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-gray-200 font-semibold"
            >
              <option value="all">همه درخواست‌ها ({requests.length})</option>
              <option value="pending">در انتظار ({pendingCount})</option>
              <option value="approved">تایید شده ({approvedCount})</option>
              <option value="rejected">رد شده ({rejectedCount})</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1">
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <FaSearch className="text-primary-500" />
              جستجو
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="جستجو بر اساس نام، موبایل یا عنوان طرح..."
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-gray-200"
            />
          </div>
        </div>

        {/* Results Info */}
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          نمایش {filteredRequests.length > 0 ? startIndex + 1 : 0} تا {Math.min(endIndex, filteredRequests.length)} از {filteredRequests.length} درخواست
        </div>
      </div>

      {/* Requests List */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
          <FaCertificate className="text-primary-500" />
          لیست درخواست‌ها
        </h3>
        
        {filteredRequests.length === 0 ? (
          <div className="text-center p-12 bg-gray-50 dark:bg-gray-700 rounded-xl">
            <FaSearch className="text-5xl text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-500 dark:text-gray-400 font-semibold">
              {searchTerm ? 'نتیجه‌ای یافت نشد' : 'درخواستی وجود ندارد'}
            </p>
            {searchTerm && (
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                لطفاً عبارت جستجو را تغییر دهید
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedRequests.map((req) => (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg border-2 ${
                  req.status === 'pending' 
                    ? 'border-yellow-300 dark:border-yellow-700' 
                    : req.status === 'approved'
                    ? 'border-green-300 dark:border-green-700'
                    : 'border-red-300 dark:border-red-700'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {getStatusBadge(req.status)}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(req.requested_at)}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">متقاضی</p>
                    <p className="font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <FaUser className="text-primary-500 text-sm" />
                      {req.registration_info?.first_name} {req.registration_info?.last_name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-mono mt-1">{req.user_mobile}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">هسته فناور</p>
                    <p className="font-bold text-gray-800 dark:text-gray-200">
                      {req.registration_info?.tech_core_name}
                    </p>
                  </div>
                </div>
                
                <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-4 mb-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">عنوان طرح</p>
                  <p className="font-bold text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
                    {req.registration_info?.idea_title}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    <span className="font-semibold">دسته:</span> {req.registration_info?.category_main_name || req.registration_info?.category_main}
                  </p>
                </div>
                
                {req.status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApprove(req.id)}
                      disabled={processing === req.id}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`approve-btn-${req.id}`}
                    >
                      {processing === req.id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          در حال تایید...
                        </>
                      ) : (
                        <>
                          <FaCheck />
                          تایید گواهی
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleReject(req.id)}
                      disabled={processing === req.id}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`reject-btn-${req.id}`}
                    >
                      {processing === req.id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          در حال رد...
                        </>
                      ) : (
                        <>
                          <FaTimes />
                          رد درخواست
                        </>
                      )}
                    </button>
                  </div>
                )}
                
                {req.reviewed_at && (
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
                    بررسی شده در: {formatDate(req.reviewed_at)}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
          >
            قبلی
          </button>
          
          <div className="flex gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-lg font-bold transition-all ${
                  currentPage === page
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
          >
            بعدی
          </button>
        </div>
      )}
    </div>
  );
}

export default CertificateManagement;
